<template>
  <div class="fb">
    <div class="fb__bar">
      <!-- 预设 / 已保存视图：一键套用整组筛选 -->
      <el-dropdown class="fb__views" trigger="click" @command="onViewCommand">
        <el-button size="small" icon="el-icon-collection-tag">
          {{ activeViewName || $t("searchOrder.views") }}<i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <template v-if="presets.length">
            <el-dropdown-item disabled class="fb__views-label">{{ $t("searchOrder.presets") }}</el-dropdown-item>
            <el-dropdown-item v-for="p in presets" :key="p.id" :command="{ type: 'preset', id: p.id }">
              {{ p.name }}
            </el-dropdown-item>
          </template>

          <template v-if="savedViews.length">
            <el-dropdown-item :divided="presets.length > 0" disabled class="fb__views-label">
              {{ $t("searchOrder.myViews") }}
            </el-dropdown-item>
            <el-dropdown-item v-for="(v, i) in savedViews" :key="'v' + i" :command="{ type: 'saved', index: i }">
              <span class="fb__views-row">
                <span>{{ v.name }}</span>
                <i
                  class="el-icon-delete fb__views-del"
                  :title="$t('searchOrder.deleteView')"
                  @click.stop="deleteView(i)"
                ></i>
              </span>
            </el-dropdown-item>
          </template>

          <el-dropdown-item :divided="presets.length > 0 || savedViews.length > 0" :command="{ type: 'save' }">
            <i class="el-icon-plus"></i> {{ $t("searchOrder.saveView") }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <!-- 页面自有的按钮（导出、批量操作…） -->
      <slot name="lead"></slot>

      <!-- 每个可检索字段一个独立输入框；同时填写多个即多条件叠加（服务端 AND） -->
      <el-input
        v-for="f in fields"
        :key="f.value"
        class="fb__field-input"
        size="small"
        clearable
        v-model="search[f.value]"
        :placeholder="f.label"
        @keyup.enter.native="submit"
        @clear="submit"
      ></el-input>

      <!-- 页面自有的下拉/日期筛选 -->
      <slot name="filters"></slot>

      <el-button size="small" type="primary" icon="el-icon-search" @click="submit">
        {{ $t("common.search") }}
      </el-button>
      <el-button size="small" icon="el-icon-refresh" @click="reset">{{ $t("common.reset") }}</el-button>

      <slot name="trail"></slot>
    </div>

    <!-- 生效中的字段：直接由已填写的输入框推导，逐个可清除 -->
    <div v-if="activeFilters.length" class="fb__chips">
      <el-tag
        v-for="c in activeFilters"
        :key="c.field"
        size="small"
        type="info"
        closable
        @close="removeFilter(c.field)"
      >
        <span class="fb__chip-field">{{ fieldLabelOf(c.field) }}:</span> {{ c.value }}
      </el-tag>
      <el-button type="text" size="mini" class="fb__chips-clear" @click="clearFilters">
        {{ $t("searchOrder.clearAll") }}
      </el-button>
    </div>
  </div>
</template>

<script>
  export default {
    name: "VtmFilterBar",
    props: {
      // 可叠加的文本检索字段：[{ value: 后端参数名, label }]
      fields: { type: Array, required: true },
      // 页面的检索对象，本组件直接写入（与既有页面写法保持一致）
      search: { type: Object, required: true },
      // 声明式预设：[{ id, name, set: { payState: 3 }, range: 'lastMonth' }]
      presets: { type: Array, default: () => [] },
      // 除文本字段外，也要随视图保存/恢复的键（下拉筛选等）
      extraKeys: { type: Array, default: () => [] },
      // 可选的日期区间（数组，本组件用 splice 改写内容而不重新赋值 prop）
      dateTime: { type: Array, default: null },
      // 每个页面独立的视图存储命名空间
      storageKey: { type: String, required: true }
    },
    data() {
      return {
        savedViews: [],
        activeViewName: ""
      };
    },
    computed: {
      // 生效中的字段直接由 search 推导——输入框就是唯一状态源，不再另存一份
      activeFilters() {
        return this.fields
          .map(f => ({ field: f.value, value: (this.search[f.value] || "").toString().trim() }))
          .filter(c => c.value);
      },
      // 本地缓存键：仅作为服务端不可用时的回退
      viewsStorageKey() {
        return "vtm-views_" + this.storageKey + "_" + (this.$cookies.get("uid") || "");
      }
    },
    created() {
      // 先用本地缓存立即渲染，再用服务端数据覆盖（服务端为准）
      this.savedViews = this.loadLocalViews();
      this.fetchViews();
    },
    methods: {
      fieldLabelOf(field) {
        const f = this.fields.find(x => x.value === field);
        return f ? f.label : field;
      },

      // ---------- 字段筛选 ----------
      removeFilter(field) {
        this.$set(this.search, field, "");
        this.activeViewName = "";
        this.submit();
      },
      clearFilters() {
        this.fields.forEach(f => this.$set(this.search, f.value, ""));
        this.activeViewName = "";
        this.submit();
      },
      // 输入框直接写入 search。带 list 参数的字段支持一次查多个值：
      // 用逗号分隔（如两台机器号），拆成数组写入对应的列表参数，后端按 OR 匹配。
      applyFilters() {
        this.fields.forEach(f => {
          if (!f.list) {
            return;
          }
          const parts = (this.search[f.value] || "")
            .toString()
            .split(/[,，\n]/)
            .map(v => v.trim())
            .filter(Boolean);
          this.$set(this.search, f.list, parts.length > 1 ? parts : []);
        });
      },

      // ---------- 日期 ----------
      setRange(kind) {
        if (!this.dateTime || !kind) return;
        const now = new Date();
        let start, end;
        if (kind === "thisMonth" || kind === "lastMonth") {
          const off = kind === "lastMonth" ? -1 : 0;
          start = new Date(now.getFullYear(), now.getMonth() + off, 1, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth() + off + 1, 0, 23, 59, 59);
        } else {
          const days = kind === "last7" ? 7 : 30;
          end = new Date();
          end.setHours(23, 59, 59, 999);
          start = new Date();
          start.setDate(start.getDate() - (days - 1));
          start.setHours(0, 0, 0, 0);
        }
        this.dateTime.splice(0, this.dateTime.length, start, end);
      },

      // ---------- 视图 ----------
      onViewCommand(cmd) {
        if (!cmd) return;
        if (cmd.type === "preset") this.applyPreset(cmd.id);
        else if (cmd.type === "saved") this.applySavedView(cmd.index);
        else if (cmd.type === "save") this.saveCurrentView();
      },
      applyPreset(id) {
        const p = this.presets.find(x => x.id === id);
        if (!p) return;
        // 先清掉字段输入与附加键，再套用预设值，避免残留
        this.fields.forEach(f => this.$set(this.search, f.value, ""));
        // 多选字段要清成 []，清成 "" 会让后端解析失败（列表/导出报错）
        this.extraKeys.forEach(k => {
          this.search[k] = Array.isArray(this.search[k]) ? [] : "";
        });
        Object.keys(p.set || {}).forEach(k => {
          this.search[k] = Array.isArray(p.set[k]) ? p.set[k].slice() : p.set[k];
        });
        this.setRange(p.range);
        this.activeViewName = p.name;
        this.submit();
      },
      loadLocalViews() {
        try {
          const raw = JSON.parse(localStorage.getItem(this.viewsStorageKey));
          return Array.isArray(raw) ? raw : [];
        } catch (e) {
          return [];
        }
      },
      cacheViewsLocally() {
        try {
          localStorage.setItem(this.viewsStorageKey, JSON.stringify(this.savedViews));
        } catch (e) {
          /* 隐私模式等场景忽略写入失败 */
        }
      },

      // 服务端保存在 sys_config 的一条记录里（type=FILTER_VIEW），
      // content 形如 {"machines":[...],"orders":[...]}，按 storageKey 分页面存放
      fetchViews() {
        this.$api
          .request(this.$url.FilterView.get, this.$method.get, { type: "FILTER_VIEW" })
          .then(res => {
            // systemConfig/get 返回的 data 就是解析后的内容对象本身（不是记录，也没有 content 字段）
            if (res.code !== 0 || !res.data) {
              return;
            }
            const mine = res.data[this.storageKey];
            if (Array.isArray(mine)) {
              this.savedViews = mine;
              this.cacheViewsLocally();
            }
          })
          .catch(() => {
            /* 取不到就继续用本地缓存 */
          });
      },
      // 读取整份配置再只替换本页面的部分，避免覆盖另一个列表页保存的视图
      persistViews() {
        this.cacheViewsLocally();
        this.$api
          .request(this.$url.FilterView.get, this.$method.get, { type: "FILTER_VIEW" })
          .then(res => {
            //先读回整份配置，只替换本页面那一段，避免把另一个列表页的视图覆盖掉
            let all = {};
            if (res.code === 0 && res.data && typeof res.data === "object") {
              all = { ...res.data };
            }
            all[this.storageKey] = this.savedViews;
            return this.$api.request(this.$url.FilterView.save, this.$method.post, {
              type: "FILTER_VIEW",
              content: JSON.stringify(all)
            });
          })
          .catch(() => {
            this.$message.warning(this.$t("searchOrder.viewSaveFailed"));
          });
      },
      saveCurrentView() {
        this.$prompt(this.$t("searchOrder.saveViewPrompt"), this.$t("searchOrder.saveView"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          inputValidator: v => (v && v.trim() ? true : this.$t("searchOrder.saveViewPrompt"))
        })
          .then(({ value }) => {
            const name = value.trim();
            const extra = {};
            this.extraKeys.forEach(k => {
              extra[k] = this.search[k];
            });
            const view = {
              name,
              filters: this.activeFilters.map(c => ({ ...c })),
              extra,
              // 存 ISO 字符串，JSON 化后仍能还原
              start: this.dateTime && this.dateTime[0] ? new Date(this.dateTime[0]).toISOString() : "",
              end: this.dateTime && this.dateTime[1] ? new Date(this.dateTime[1]).toISOString() : ""
            };
            const at = this.savedViews.findIndex(v => v.name === name);
            if (at >= 0) this.savedViews.splice(at, 1, view);
            else this.savedViews.push(view);
            this.persistViews();
            this.activeViewName = name;
            this.$message.success(this.$t("searchOrder.viewSaved"));
          })
          .catch(() => {});
      },
      applySavedView(index) {
        const v = this.savedViews[index];
        if (!v) return;
        // 视图里保存的是各字段的值，直接写回对应输入框
        this.fields.forEach(f => this.$set(this.search, f.value, ""));
        // 只还原当前页面还有的字段：旧视图里已下线的字段（如交易页的机器名）不能变成看不见、清不掉的筛选
        (v.filters || [])
          .filter(c => this.fields.some(f => f.value === c.field))
          .forEach(c => this.$set(this.search, c.field, c.value));
        this.extraKeys.forEach(k => {
          if (v.extra && v.extra[k] !== undefined) {
            this.search[k] = Array.isArray(v.extra[k]) ? v.extra[k].slice() : v.extra[k];
          } else {
            this.search[k] = Array.isArray(this.search[k]) ? [] : "";
          }
        });
        if (this.dateTime && v.start && v.end) {
          this.dateTime.splice(0, this.dateTime.length, new Date(v.start), new Date(v.end));
        }
        this.activeViewName = v.name;
        this.submit();
      },
      deleteView(index) {
        const removed = this.savedViews[index];
        this.savedViews.splice(index, 1);
        this.persistViews();
        if (removed && this.activeViewName === removed.name) this.activeViewName = "";
        this.$message.success(this.$t("searchOrder.viewDeleted"));
      },

      // ---------- 对外 ----------
      submit() {
        this.applyFilters();
        this.$emit("submit");
      },
      reset() {
        this.fields.forEach(f => this.$set(this.search, f.value, ""));
        this.activeViewName = "";
        this.$emit("reset");
      },
      // 供父组件在导出等场景下先同步筛选条件
      syncFilters() {
        this.applyFilters();
      }
    }
  };
</script>

<style scoped lang="scss">
  .fb__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    // 每个字段一个输入框，数量较多时换行显示，避免横向滚动把筛选藏起来
    flex-wrap: wrap;
    width: 100%;
    row-gap: 8px;
  }

  .fb__views {
    flex-shrink: 0;
  }

  // 每个字段一个输入框：窄一些，多个并排也放得下，窄屏自动换行
  .fb__field-input {
    width: 190px;
    min-width: 150px;
    flex: 0 1 190px;
  }

  .fb__chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }

  .fb__chip-field {
    color: var(--vtm-ink-3);
    margin-right: 2px;
  }

  .fb__chips-clear {
    margin-left: 2px;
    padding: 0 4px;
  }

  .fb__views-label {
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--vtm-ink-3);
  }

  .fb__views-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .fb__views-del {
    color: var(--vtm-ink-3);

    &:hover {
      color: var(--vtm-crit);
    }
  }
</style>
